import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { get } from '@/lib/api-client'

/**
 * Dashboard metrics response type
 */
export interface DashboardMetrics {
  attributeCount: number
  policyCount: number
  activeCount: number
  draftCount: number
  recentActivityCount: number
  lastActivityTimestamp: string | null
  statusBreakdown?: Record<string, number>
  categoryBreakdown?: Record<string, number>
}

/**
 * Placeholder data for initial development
 */
const placeholderData: DashboardMetrics = {
  attributeCount: 0,
  policyCount: 0,
  activeCount: 0,
  draftCount: 0,
  recentActivityCount: 0,
  lastActivityTimestamp: null,
  statusBreakdown: {},
  categoryBreakdown: {},
}

/**
 * Hook to fetch dashboard metrics from API
 * Uses TanStack Query with placeholder data for initial loading
 */
export function useDashboardMetrics(): UseQueryResult<DashboardMetrics, Error> {
  return useQuery({
    queryKey: ['dashboard', 'metrics'],
    queryFn: async () => {
      try {
        return await get<DashboardMetrics>('/dashboard/metrics')
      } catch {
        // Return placeholder data if API is not available
        return placeholderData
      }
    },
    placeholderData,
    // Refetch every 5 minutes
    refetchInterval: 5 * 60 * 1000,
    // Consider data stale after 1 minute
    staleTime: 60 * 1000,
  })
}
